import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { downloadAstroPdf } from "@/lib/astro";
import {
  Panel, ScoreRing, Spectrum, NoteWheel, CategoryRadar, StatusDonut, StrengthBars, StatusPill, vizColor,
} from "@/components/viz/ReportViz";
import RemedyCrossSell from "@/components/RemedyCrossSell";

const NOTES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

const CROSS_SELL = {
  money: ["Prosperity", "Money"],
  career: ["Career"],
  relationship: ["Relationship"],
};

export default function AstroReportNakshatra({ scanId, type, report, grid }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const analysis = report.section3_analysis || report.section4_analysis || {};
  const concern = report.section4_areas_of_concern || report.section5_areas_of_concern || {};
  const detailed = report.section6_detailed_analysis || report.section7_detailed_analysis || [];
  const reco = report.section9_career_recommendations;
  const dosDonts = report.section10_dos_donts;
  const overall = analysis.overall_score ?? 0;
  const statusMap = analysis.status_map || [];
  const breakdown = analysis.status_breakdown || {};
  const categoryBreakdown = analysis.category_breakdown || null;

  const spectrum = statusMap.map((n) => ({ name: n.nakshatra, value: n.score }));
  const octave4 = (grid?.["4"] || []);
  const noteWheel = NOTES.map((nt) => {
    const cell = octave4.find((c) => c.note === nt);
    return { note: nt, value: cell ? Math.round(cell.hits) : 0 };
  });
  const catData = categoryBreakdown
    ? Object.entries(categoryBreakdown).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), score: Math.round(v.score || 0) }))
    : null;

  const doPdf = async () => {
    setPdfLoading(true);
    try { await downloadAstroPdf(scanId, type); }
    catch { toast.error("Could not generate PDF."); }
    finally { setPdfLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid={`astro-report-${type}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl tracking-tight text-slate-50" style={{ fontFamily: "Outfit, sans-serif" }}>{report.report_title}</h2>
          <p className="text-sm text-slate-400">{report.subtitle}</p>
        </div>
        <Button onClick={doPdf} disabled={pdfLoading} className="rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/20" data-testid={`astro-pdf-${type}`}>
          {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} strokeWidth={1.5} />} Download PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Overall frequency" className="flex flex-col items-center justify-center gap-4">
          <ScoreRing value={overall} />
          <StatusDonut breakdown={breakdown} />
        </Panel>
        <Panel title="Frequency spectrum" className="lg:col-span-2">
          <Spectrum data={spectrum} color="#22D3EE" />
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Octave-4 note wheel"><NoteWheel data={noteWheel} color="#8B5CF6" /></Panel>
        {catData
          ? <Panel title="Category balance"><CategoryRadar data={catData} /></Panel>
          : <Panel title="Nakshatra strength"><StrengthBars rows={statusMap.map((n) => ({ name: n.nakshatra, score: n.score, status: n.status }))} /></Panel>}
      </div>

      {CROSS_SELL[type] && <RemedyCrossSell keywords={CROSS_SELL[type]} />}

      {catData && (
        <Panel title="Nakshatra strength"><StrengthBars rows={statusMap.map((n) => ({ name: n.nakshatra, score: n.score, status: n.status }))} /></Panel>
      )}

      <Panel title="Status map" className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-white/10">
              <th className="py-2 pr-3 font-medium">Nakshatra</th><th className="py-2 pr-3 font-medium">Planet</th>
              <th className="py-2 pr-3 font-medium">Score</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 font-medium">Archetype</th>
            </tr>
          </thead>
          <tbody>
            {statusMap.map((n) => (
              <tr key={n.nakshatra} className="border-b border-white/[0.04]" data-testid={`row-${n.nakshatra}`}>
                <td className="py-2 pr-3 text-slate-200">{n.nakshatra}</td>
                <td className="py-2 pr-3 text-slate-400">{n.planet}</td>
                <td className="py-2 pr-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>{n.score}%</td>
                <td className="py-2 pr-3"><StatusPill status={n.status} /></td>
                <td className="py-2 text-slate-400">{n.archetype}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {concern.priority_list?.length > 0 && (
        <Panel title="Areas of concern">
          <div className="grid md:grid-cols-2 gap-3">
            {concern.priority_list.map((a) => (
              <div key={a.priority} className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid={`concern-${a.priority}`}>
                <span className="grid place-items-center h-7 w-7 rounded-full text-xs shrink-0" style={{ background: vizColor(a.status) + "1a", color: vizColor(a.status) }}>{a.priority}</span>
                <div>
                  <p className="text-sm text-slate-200"><b>{a.nakshatra}</b> · {a.current_score}% → target {a.target_score}%</p>
                  <p className="text-xs text-slate-400 mt-1">{a.impact}</p>
                </div>
              </div>
            ))}
          </div>
          {concern.combined_impact && <p className="text-xs text-slate-400 mt-4 leading-relaxed">{concern.combined_impact}</p>}
        </Panel>
      )}

      {reco && reco.primary?.category && (
        <Panel title="Recommended career paths" className="border-cyan-400/20 bg-gradient-to-br from-cyan-400/[0.06] to-[#0C1017]" testid="career-recommendations">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-400 mb-1">Primary direction</p>
              <h3 className="text-xl mb-1 text-slate-50" style={{ fontFamily: "Outfit, sans-serif" }}>{reco.primary.category} <span className="text-cyan-300 text-base">· {reco.primary.score}%</span></h3>
              <p className="text-xs text-slate-400 leading-relaxed mb-3">{reco.primary.why_fits}</p>
              {reco.secondary?.category && <p className="text-xs text-slate-400"><span className="text-slate-200">Secondary:</span> {reco.secondary.category} · {reco.secondary.score}%</p>}
              {reco.caution_categories?.length > 0 && <p className="text-xs text-slate-400 mt-1"><span className="text-slate-200">Approach with care:</span> {reco.caution_categories.map((c) => c.category).join(", ")}</p>}
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-2">Top career matches</p>
              <StrengthBars rows={(reco.top_5_careers || []).map((c) => ({ name: c.title, score: c.match }))} />
            </div>
          </div>
          {(dosDonts?.dos?.length > 0 || dosDonts?.donts?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-6 mt-6 pt-5 border-t border-white/10">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-amber-300 mb-2">Do</p>
                <ul className="space-y-1.5 text-xs text-slate-400">{dosDonts.dos.map((d, i) => <li key={i}>• {d}</li>)}</ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-red-400 mb-2">Don't</p>
                <ul className="space-y-1.5 text-xs text-slate-400">{dosDonts.donts.map((d, i) => <li key={i}>• {d}</li>)}</ul>
              </div>
            </div>
          )}
        </Panel>
      )}

      <div>
        <h3 className="text-xl mb-4 text-slate-50" style={{ fontFamily: "Outfit, sans-serif" }}>Detailed analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {detailed.map((n, i) => (
            <motion.div key={n.nakshatra} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
              className="rounded-2xl border border-white/[0.06] bg-[#0C1017] p-5" data-testid={`detail-${n.nakshatra}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-lg text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>{n.nakshatra}</span>
                <StatusPill status={n.status} score={n.score} />
              </div>
              <p className="text-xs text-slate-400 mb-3">{n.archetype} · {n.planet}</p>
              <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden mb-3">
                <div className="h-full rounded-full" style={{ width: `${n.score}%`, background: vizColor(n.status), boxShadow: `0 0 8px ${vizColor(n.status)}88` }} />
              </div>
              <div className="text-xs text-slate-400 space-y-1">
                <p><span className="text-slate-300">Golden rule:</span> {n.golden_rule}</p>
                <p><span className="text-slate-300">When honored:</span> {n.when_honored}</p>
                <p><span className="text-slate-300">When violated:</span> {n.when_violated}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
